import React from 'react';
import { FillLabel } from './FillLabel';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronsDownIcon } from 'lucide-react';
import { ParticleFieldBackdrop } from './ParticleFieldBackdrop';
import { GridRails } from './GridRails';
import { GridDivider } from './GridDivider';
import type { HoverMode } from '../utils/ditherField';
const EASE = [0.22, 1, 0.36, 1] as const;
type HeroProps = {
  hoverMode?: HoverMode;
};
export function Hero({
  hoverMode = 'trail'
}: HeroProps) {
  const reduceMotion = useReducedMotion();
  return <section className="relative flex h-[100svh] min-h-[760px] max-h-[1080px] w-full flex-col overflow-hidden bg-navy" data-id="element-274" data-mp="x8b6zmndx72b">
      <ParticleFieldBackdrop hoverMode={hoverMode} data-id="element-275" data-mp="nc85mj0cxcgf" />
      <GridRails tone="dark" data-id="element-276" data-mp="nboov5mg0u3z" />

      <div className="relative z-10 mx-auto flex w-full max-w-[1720px] flex-1 flex-col items-start justify-start px-5 pt-[170px] md:px-16 md:pt-[210px] lg:px-24" data-id="element-277" data-mp="nekl8pin7aqb">
        <motion.p initial={reduceMotion ? false : {
        opacity: 0,
        y: 14,
        filter: 'blur(4px)'
      }} animate={{
        opacity: 1,
        y: 0,
        filter: 'blur(0px)'
      }} transition={{
        duration: 0.9,
        delay: 0.25,
        ease: EASE
      }} className="mb-6 flex items-start font-accent text-[11px] uppercase leading-[1.6] tracking-[0.08em] text-cream/80" data-id="element-278" data-mp="380vy3velgpc3">
          <motion.span aria-hidden="true" className="mr-3 mt-[6px] inline-block h-[6px] w-[6px] shrink-0 bg-white" animate={reduceMotion ? undefined : {
          opacity: [1, 1, 0.15, 0.15, 1]
        }} transition={{
          duration: 1.6,
          times: [0, 0.45, 0.5, 0.95, 1],
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-279" data-mp="3e8whmjcz6oiq" />
          {/* Place and coordinates stay intact: on narrow screens the coordinates drop to their own line */}
          <span className="flex flex-col sm:flex-row sm:items-center" data-id="element-280" data-mp="2vgu1ra85rc9j">
            <span className="whitespace-nowrap" data-id="element-281" data-mp="2hkgh5s8buuqu">Dubai, United Arab Emirates</span>
            <span aria-hidden="true" className="mx-2 hidden sm:inline" data-id="element-282" data-mp="2ljpbay7a4umx">·</span>
            <span className="whitespace-nowrap" data-id="element-283" data-mp="s2jmpvqkdyiz">25.20485° N, 55.27078° E</span>
          </span>
        </motion.p>
        <motion.h1 initial={reduceMotion ? false : {
        opacity: 0,
        y: 14,
        filter: 'blur(4px)'
      }} animate={{
        opacity: 1,
        y: 0,
        filter: 'blur(0px)'
      }} transition={{
        duration: 0.9,
        delay: 0.35,
        ease: EASE
      }} className="max-w-[1040px] font-display text-[50px] font-normal leading-[0.96] tracking-[-0.04em] text-cream sm:text-[72px] lg:text-[96px]" data-id="element-284" data-mp="nb1jt24zdnvd">
          A Dubai family office,{' '}
          <span className="font-editorial italic tracking-[-0.02em]" data-id="element-285" data-mp="162c8rdr5bqkm">compounding quality</span>
        </motion.h1>
      </div>


      <motion.div className="relative z-10" initial={reduceMotion ? false : {
      opacity: 0,
      y: 14,
      filter: 'blur(4px)'
    }} animate={{
      opacity: 1,
      y: 0,
      filter: 'blur(0px)'
    }} transition={{
      duration: 0.9,
      delay: 0.5,
      ease: EASE
    }} data-id="element-286" data-mp="114kkctppmml6">
        <GridDivider tone="dark" data-id="element-287" data-mp="3hpsen5c8br21" />
        <div className="mx-auto grid w-full max-w-[1720px] grid-cols-1 items-center gap-6 px-5 pb-10 pt-8 md:grid-cols-[1fr_auto_1fr] md:px-16 md:pb-12 md:pt-10 lg:px-24" data-id="element-288" data-mp="3htpwlnn2mckc">
          <p className="max-w-[500px] text-[15px] leading-[1.35] text-cream/80" data-id="element-289" data-mp="21b64lzv0l4np">
            A Dubai-registered family office ecosystem — an infrastructure of optimal conditions for Culture, Capital and Longevity.
          </p>
          <a href="#about" aria-label="Scroll down" className="hidden justify-self-center text-cream/35 transition-colors duration-150 hover:text-cream md:block" data-id="element-290" data-mp="2fzsugvi9pet6">
            <ChevronsDownIcon className="h-6 w-6" strokeWidth={1.25} data-id="element-291" data-mp="2kxc75qzbhjj0" />
          </a>
          <div className="flex gap-5 md:justify-self-end" data-id="element-292" data-mp="2p1scnr50qoxp">
            <a href="#contact" className="btn-fill btn-fill-light flex h-[46px] items-center justify-center whitespace-nowrap border border-cream/30 px-7 font-accent text-[11px] uppercase tracking-[0.08em] text-cream/90" data-id="element-293" data-mp="2tzbq4otw6xol">
              <FillLabel data-id="element-294" data-mp="zlstdh7orj4c">Partner with us</FillLabel>
            </a>
          </div>
        </div>
      </motion.div>
    </section>;
}